// R-131-FIN-DECISION-A — onshore wind + BESS pathway preset (NREL ATB).

import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import type { Pathway } from '../types';
import {
  DEFAULT_BASE_WACC_PCT,
  DEFAULT_BESS_DURATION_HOURS,
  annualMwhFromCf,
  buildCapexBlock,
  buildStandardBtmPhases,
  capacityKw,
  computeAnnualOpexUsd,
  mergePathway,
  pathwayIdFromTechnology,
  resolveFacilityLoadMw,
  resolveNameplateMw,
  resolveProjectStartDate,
  valueOfServedLoadRevenue,
  type PathwayPresetOptions,
} from './shared';

/** Class-4 onshore wind + 4-hr BESS — NREL ATB 2024 onshore wind + Lazard LCOE. */
export const WIND_BESS_BENCHMARKS = {
  turbineCapexPerKwUsd: 1700,
  bessCapexPerKwhUsd: 400,
  fixedOmPerKwYearUsd: 38,
  variableOmPerMwhUsd: 0,
  designLifeYears: 25,
  availabilityPct: 35,
  capacityFactorPct: 38,
  constructionMonths: 24,
  riskPremiumBps: 0,
  bessAugmentationYears: [10, 20] as const,
  bessAugmentationPctOfBessCapex: 0.15,
  contingencyPct: 8,
  residualPctOfBessCapex: 0.1,
} as const;

export function computeWindBessCapexUsd(capacityMw: number, bessDurationHours: number): {
  turbineCapexUsd: number;
  bessCapexUsd: number;
  totalCapexUsd: number;
} {
  const kw = capacityKw(capacityMw);
  const turbineCapexUsd = kw * WIND_BESS_BENCHMARKS.turbineCapexPerKwUsd;
  const bessKwh = kw * bessDurationHours;
  const bessCapexUsd = bessKwh * WIND_BESS_BENCHMARKS.bessCapexPerKwhUsd;
  return { turbineCapexUsd, bessCapexUsd, totalCapexUsd: turbineCapexUsd + bessCapexUsd };
}

export function presetFor(
  intent: ProjectIntent,
  _siteContext: SiteContext,
  options?: PathwayPresetOptions,
): Pathway {
  // Generation NAMEPLATE: facility LOAD grossed up to a plant that SERVES the
  // firm 24/7 load. Wind is intermittent with a LOW capacity credit on a flat
  // 24/7 load, so the nameplate is a real overbuild (≈ load × 3.8 — firm tier
  // binds at 30% ELCC + reserve). Capex / energy / O&M ride this nameplate.
  const facilityLoadMw = resolveFacilityLoadMw(intent);
  const capacityMw = resolveNameplateMw(intent, 'wind-bess');
  const startDate = resolveProjectStartDate(intent);
  const b = WIND_BESS_BENCHMARKS;
  const bessDurationHours = DEFAULT_BESS_DURATION_HOURS;

  const { bessCapexUsd, totalCapexUsd: baseCapexUsd } = computeWindBessCapexUsd(
    capacityMw,
    bessDurationHours,
  );
  const augUsd = bessCapexUsd * b.bessAugmentationPctOfBessCapex;
  const annualMwh = annualMwhFromCf(capacityMw, b.capacityFactorPct);
  const annualOpexUsd = computeAnnualOpexUsd(
    capacityMw,
    b.fixedOmPerKwYearUsd,
    b.variableOmPerMwhUsd,
    0,
    annualMwh,
  );

  const capex = buildCapexBlock(startDate, b.constructionMonths, baseCapexUsd, {
    contingencyPct: b.contingencyPct,
  });

  const codYear =
    new Date(startDate).getUTCFullYear() + Math.ceil(b.constructionMonths / 12);

  const base: Pathway = {
    id: pathwayIdFromTechnology('wind-bess'),
    name: 'BTM Wind + BESS',
    classification: 'btm',
    technology: 'wind-bess',
    capacityMW: capacityMw,
    facilityLoadMW: facilityLoadMw,
    phases: buildStandardBtmPhases({
      startDate,
      constructionMonths: b.constructionMonths,
      designLifeYears: b.designLifeYears,
      totalCapexUsd: baseCapexUsd,
      decommissioningUsd: 0,
      annualMwh,
      annualOpexUsd,
    }),
    capex,
    opex: {
      fixedOmPerKwYearUsd: b.fixedOmPerKwYearUsd,
      variableOmPerMwhUsd: b.variableOmPerMwhUsd,
      fuelCostPerMwhUsd: 0,
      refurbishmentEvents: b.bessAugmentationYears.map((yearFromCod) => ({
        yearFromCod,
        usd: augUsd,
        description: `BESS augmentation at year ${yearFromCod} (15% of BESS CAPEX)`,
      })),
    },
    // VALUE-CONSISTENT credit — the SAME value-of-served-load price AND the same
    // escalation as every other pathway.
    //
    // C15 — this used to override the escalator to 1.5%/yr, a leftover from when
    // the field was a technology PPA price (a solar PPA genuinely escalates
    // slower than a gas one). It is no longer a PPA: it is the value of the LOAD
    // SERVED, and the same load is worth the same to the same datacenter however
    // it is generated. Escalating it more slowly for solar and wind quietly
    // penalised exactly the two pathways whose cost advantage is long-dated,
    // over a 30-40 year horizon where a 0.5pt escalator gap compounds to ~15%.
    revenue: valueOfServedLoadRevenue({
      termYears: b.designLifeYears,
      availabilityPct: b.availabilityPct,
    }),
    risk: {
      technologyMaturity: 'mature',
      regulatoryRisk: 'low',
      fuelPriceRisk: 'none',
      baseWaccPct: DEFAULT_BASE_WACC_PCT,
      riskPremiumBps: b.riskPremiumBps,
    },
    carbon: {
      tCo2ePerMwh: 0,
    },
    lifetime: {
      designLifeYears: b.designLifeYears,
      decommissioningUsd: 0,
      decommissioningYear: codYear + b.designLifeYears,
      residualValueUsd: bessCapexUsd * b.residualPctOfBessCapex,
    },
  };

  return mergePathway(base, options?.overrides);
}
